import React, { memo, useEffect, useState } from "react";
import {
  Stack,
  Typography,
  CardContent,
  Card,
  Chip,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Tooltip,
  Box,
  Icon,
  TableContainer,
} from "investira.react.components";
import { formats, validators } from "investira.sdk";

import { useAtivosContext } from "../../../contexts/AtivosContext";
import services from "../../../services";

const AtivoIndicesRentabilidade = memo(() => {
  const { state, actions } = useAtivosContext();
  const { pessoa, ativo, cotacao } = state;

  const [rentabilidade, setRentabilidade] = useState([]);
  const [rentabilidadeReal, setRentabilidadeReal] = useState([]);
  const [variacao, setVariacao] = useState([]);
  const [indiceSharpe, setIndiceSharpe] = useState([]);

  useEffect(() => {
    if (!validators.isEmpty(cotacao.historico)) {
      const xHistoricos = Object.values(cotacao.historico);

      const xRentabilidade = xHistoricos.map((xHistorico) => {
        return actions.calcularDiffNoPeriodo(xHistorico, "cotacao_ajustada");
      });

      const xVariacao = xHistoricos.map((xHistorico) => {
        return actions.calcularDiffNoPeriodo(
          xHistorico,
          "cotacao_ajustada_ex_receitas"
        );
      });

      setRentabilidade(xRentabilidade);
      setVariacao(xVariacao);

      const xDataInicio = cotacao.historico.full[0]?.data;

      if (xDataInicio) {
        services.indicadores.cotacao(
          {
            indicador_id: "IPCA",
            data_inicio: xDataInicio,
            cotacao_tip_id: "1,4",
          },
          (rRes) => {
            const xData = rRes.data;
            const xHistoric = Object.values(actions.createHistoric(xData));

            const xIpca = xHistoric.map((xHistorico) => {
              return actions.calcularDiffNoPeriodo(
                xHistorico,
                "variacao_acumulada"
              );
            });

            // console.log("xIpca", xIpca);

            const xRentabilidadeReal = xRentabilidade.map((xPercentual, i) => {
              const xFator = xPercentual / 100 + 1;
              const xIpcaFator = xIpca[i] / 100 + 1;

              // console.log("xIpca[i]", xIpca[i]);
              // console.log("xIpcaFator", xIpcaFator);

              return (xFator / xIpcaFator - 1) * 100;
            });

            setRentabilidadeReal(xRentabilidadeReal);
          },
          (rErr) => {
            console.log(rErr);
          }
        );
      }
    }
  }, [cotacao.historico]);

  if (validators.isEmpty(cotacao.historico.full)) {
    return null;
  }

  return (
    <Stack flexGrow={1}>
      <Card>
        <CardContent
          sx={{
            height: "100%",
            display: "flex",
            flexDirection: "column",
            justifyContent: "space-between",
          }}
        >
          <Stack spacing={2} sx={{ py: 2 }}>
            <Stack>
              <Typography
                variant="subtitle1"
                color="textPrimary"
                sx={{ fontWeight: 600 }}
              >
                Índices de Rentabilidade
              </Typography>
            </Stack>
            {/* Aqui entra os ativos */}
            <Stack direction="row" spacing={1}>
              <Chip
                label={ativo.ticker || ativo.ativo_seuid || ativo.ativo}
                variant="outlined"
                color="primary"
                size="small"
              />
            </Stack>
            <Stack direction="column" sx={{ gap: 1 }}>
              <TableContainer
                id="table-container"
                sx={{
                  overflow: "auto",
                }}
              >
                <Table sx={{ minWidth: 650 }} size="small" tableLayout="fixed">
                  <TableHead>
                    <TableRow>
                      <TableCell
                        sx={{
                          position: "sticky",
                          left: 0,
                          zIndex: 1,
                          backgroundColor: (theme) =>
                            theme.palette.secondary.dark,
                        }}
                      ></TableCell>
                      <TableCell align="right" minWidth={"100px"}>
                        <Typography
                          variant="caption"
                          color="textSecondary"
                          noWrap
                        >
                          Ano
                        </Typography>
                      </TableCell>
                      <TableCell align="right" minWidth={"100px"}>
                        <Typography
                          variant="caption"
                          color="textSecondary"
                          noWrap
                        >
                          1 Mês
                        </Typography>
                      </TableCell>
                      <TableCell align="right" minWidth={"100px"}>
                        <Typography
                          variant="caption"
                          color="textSecondary"
                          noWrap
                        >
                          6 Meses
                        </Typography>
                      </TableCell>
                      <TableCell align="right" minWidth={"100px"}>
                        <Typography
                          variant="caption"
                          color="textSecondary"
                          noWrap
                        >
                          12 Meses
                        </Typography>
                      </TableCell>
                      <TableCell align="right" minWidth={"100px"}>
                        <Typography
                          variant="caption"
                          color="textSecondary"
                          noWrap
                        >
                          24 Meses
                        </Typography>
                      </TableCell>
                      <TableCell align="right" minWidth={"100px"}>
                        <Typography
                          variant="caption"
                          color="textSecondary"
                          noWrap
                        >
                          36 Meses
                        </Typography>
                      </TableCell>
                      <TableCell align="right" minWidth={"100px"}>
                        <Typography
                          variant="caption"
                          color="textSecondary"
                          noWrap
                        >
                          48 Meses
                        </Typography>
                      </TableCell>
                      <TableCell align="right" minWidth={"100px"}>
                        <Typography
                          variant="caption"
                          color="textSecondary"
                          noWrap
                        >
                          60 Meses
                        </Typography>
                      </TableCell>
                      <TableCell align="right" minWidth={"100px"}>
                        <Typography
                          variant="caption"
                          color="textSecondary"
                          noWrap
                        >
                          Total
                        </Typography>
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    <TableRow
                      sx={{ "&:last-child td, &:last-child th": { border: 0 } }}
                    >
                      <TableCell
                        component="th"
                        scope="row"
                        sx={{
                          position: "sticky",
                          left: 0,
                          zIndex: 1,
                          backgroundColor: (theme) =>
                            theme.palette.secondary.dark,
                        }}
                      >
                        <Typography variant="body2" color="textPrimary" noWrap>
                          Rentabilidade
                        </Typography>
                      </TableCell>
                      {!validators.isEmpty(rentabilidade) &&
                        rentabilidade.map((xValue) => {
                          return (
                            <TableCell
                              align="right"
                              key={crypto.randomUUID()}
                              minWidth={"100px"}
                            >
                              {!xValue || isNaN(xValue) || !isFinite(xValue) ? (
                                <Typography variant="body2" noWrap>
                                  ---
                                </Typography>
                              ) : (
                                <Typography
                                  color={xValue < 0 && "error.light"}
                                  variant="body2"
                                  noWrap
                                >
                                  {formats.formatNumber(xValue, 2, true)} %
                                </Typography>
                              )}
                            </TableCell>
                          );
                        })}
                    </TableRow>
                    <TableRow
                      sx={{ "&:last-child td, &:last-child th": { border: 0 } }}
                    >
                      <TableCell
                        component="th"
                        scope="row"
                        sx={{
                          position: "sticky",
                          left: 0,
                          zIndex: 1,
                          backgroundColor: (theme) =>
                            theme.palette.secondary.dark,
                        }}
                      >
                        <Stack direction="row" spacing={2} alignItems="center">
                          <Typography
                            variant="body2"
                            color="textPrimary"
                            noWrap
                          >
                            Rentabilidade Real
                          </Typography>
                          <Stack alignItems="center" justifyContent="center">
                            <Tooltip
                              title={
                                "Rentabilidade já descontada a inflação (IPCA)."
                              }
                            >
                              <Box sx={{ width: "16px", height: "16px" }}>
                                <Icon
                                  iconName="information"
                                  size={16}
                                  color="primary"
                                />
                              </Box>
                            </Tooltip>
                          </Stack>
                        </Stack>
                      </TableCell>
                      {!validators.isEmpty(rentabilidadeReal) &&
                        rentabilidadeReal.map((xValue) => {
                          return (
                            <TableCell align="right" key={crypto.randomUUID()}>
                              {!xValue || isNaN(xValue) || !isFinite(xValue) ? (
                                <Typography variant="body2" noWrap>
                                  ---
                                </Typography>
                              ) : (
                                <Typography
                                  color={xValue < 0 && "error.light"}
                                  variant="body2"
                                  noWrap
                                >
                                  {formats.formatNumber(xValue, 2, true)} %
                                </Typography>
                              )}
                            </TableCell>
                          );
                        })}
                    </TableRow>
                    <TableRow
                      sx={{ "&:last-child td, &:last-child th": { border: 0 } }}
                    >
                      <TableCell
                        component="th"
                        scope="row"
                        sx={{
                          position: "sticky",
                          left: 0,
                          zIndex: 1,
                          backgroundColor: (theme) =>
                            theme.palette.secondary.dark,
                        }}
                      >
                        <Typography variant="body2" noWrap>
                          Variação
                        </Typography>
                      </TableCell>
                      {!validators.isEmpty(variacao) &&
                        variacao.map((xValue) => {
                          return (
                            <TableCell align="right" key={crypto.randomUUID()}>
                              {!xValue || isNaN(xValue) || !isFinite(xValue) ? (
                                <Typography variant="body2" noWrap>
                                  ---
                                </Typography>
                              ) : (
                                <Typography
                                  color={xValue < 0 && "error.light"}
                                  variant="body2"
                                  noWrap
                                >
                                  {formats.formatNumber(xValue, 2, true)} %
                                </Typography>
                              )}
                            </TableCell>
                          );
                        })}
                    </TableRow>
                    {/* <TableRow
                      sx={{ "&:last-child td, &:last-child th": { border: 0 } }}
                    >
                      <TableCell
                        component="th"
                        scope="row"
                        sx={{
                          position: "sticky",
                          left: 0,
                          zIndex: 1,
                          backgroundColor: (theme) =>
                            theme.palette.secondary.dark,
                        }}
                      >
                        <Typography variant="body2" noWrap>
                          Índice de Sharpe
                        </Typography>
                      </TableCell>
                      <TableCell align="right">0%</TableCell>
                      <TableCell align="right">0%</TableCell>
                      <TableCell align="right">0%</TableCell>
                      <TableCell align="right">0%</TableCell>
                      <TableCell align="right">0%</TableCell>
                      <TableCell align="right">0%</TableCell>
                      <TableCell align="right">0%</TableCell>
                      <TableCell align="right">0%</TableCell>
                      <TableCell align="right">0%</TableCell>
                    </TableRow> */}
                  </TableBody>
                </Table>
              </TableContainer>
            </Stack>
          </Stack>
        </CardContent>
      </Card>
    </Stack>
  );
});

export default AtivoIndicesRentabilidade;
